import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import isEqual from 'lodash.isequal';
import uniqBy from 'lodash.uniqby';
import uniqWith from 'lodash.uniqwith';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataFieldWithDataType,
  getKeyFromDataFieldWithDataType,
} from '../../../common-types';
import { styled } from '../../common/components/styled';
import { lightTeal } from '../../common/theme/color-constants';
import { defaultTheme } from '../../common/theme/default-theme';
import { sortAlphabeticalComparator } from '../../common/utilFunctions/sorters';
import { isDataFieldMatchedInSearch } from '../../common/utilFunctions/utils';
import { getAliasForDataField } from '../../common/v2/api/alias/utils';
import { useFieldLabelFormatter } from '../../common/v2/common/hooks';
import {
  useAliasesContext,
  useDashboardTrackerContext,
  useGlobalLocaleContext,
} from '../../common/v2/context/contexts';

interface SearchableColumnSelectorProps {
  selectedFields: DataFieldWithDataType[];
  handleApplyFields: (fields: DataFieldWithDataType[]) => void;
  onClose: () => void;
  allowedFields: DataFieldWithDataType[];
  /**
   * Legacy-view toggle. Omit it (as the Data Explorer does) and the toggle button is not
   * rendered — there is no old view to switch to.
   */
  useNewUI?: boolean;
  toggleNewUI?: () => void;
  /**
   * Fields shown but not selectable, because the role may not read them. They are greyed
   * out with a lock rather than hidden, so users can see the column exists.
   */
  disabledFields?: DataFieldWithDataType[];
  /**
   * Render the selected fields as a drag-to-reorder list instead of static chips, so the
   * order they are applied in is the order they were dragged into.
   */
  reorderable?: boolean;
}

const DenseListItem = styled(ListItemButton)`
  padding-top: 0px;
  padding-bottom: 0px;
` as typeof ListItem;

/**
 * Same row, typed as the ListItemButton it actually is, so it accepts `disabled`.
 * DenseListItem is cast to ListItem, which has no such prop.
 */
const DenseListItemButton = styled(ListItemButton)`
  padding-top: 0px;
  padding-bottom: 0px;
`;

interface GroupedColumns {
  id: string;
  nameKey: string;
  subItems?: DataFieldWithDataType[];
}

const getGroupedColumns = (allowedFields: DataFieldWithDataType[]) => {
  const groups: GroupedColumns[] = [
    {
      id: 'date',
      nameKey: 'pages:reports.columnSelector.searchableView.groupHeaders.date',
    },
    {
      id: 'name',
      nameKey: 'pages:reports.columnSelector.searchableView.groupHeaders.name',
    },
    {
      id: 'id',
      nameKey: 'pages:reports.columnSelector.searchableView.groupHeaders.id',
    },
    {
      id: 'organization',
      nameKey:
        'pages:reports.columnSelector.searchableView.groupHeaders.organization',
    },
    {
      id: 'location',
      nameKey:
        'pages:reports.columnSelector.searchableView.groupHeaders.location',
    },
    {
      id: 'function',
      nameKey:
        'pages:reports.columnSelector.searchableView.groupHeaders.function',
    },
    {
      id: 'employment-type',
      nameKey:
        'pages:reports.columnSelector.searchableView.groupHeaders.employmentType',
    },
    {
      id: 'job',
      nameKey: 'pages:reports.columnSelector.searchableView.groupHeaders.job',
    },
    {
      id: 'position',
      nameKey:
        'pages:reports.columnSelector.searchableView.groupHeaders.position',
    },
    {
      id: 'email',
      nameKey: 'pages:reports.columnSelector.searchableView.groupHeaders.email',
    },
    {
      id: 'performance',
      nameKey:
        'pages:reports.columnSelector.searchableView.groupHeaders.performance',
    },
    {
      id: 'onboarding',
      nameKey:
        'pages:reports.columnSelector.searchableView.groupHeaders.onboarding',
    },
    {
      id: 'score',
      nameKey: 'pages:reports.columnSelector.searchableView.groupHeaders.score',
    },
    {
      id: 'salary',
      nameKey:
        'pages:reports.columnSelector.searchableView.groupHeaders.salary',
    },
    {
      id: 'currency',
      nameKey:
        'pages:reports.columnSelector.searchableView.groupHeaders.currency',
    },
  ];

  let alreadyGroupedColumns: DataFieldWithDataType[] = [];
  const groupedColumns: GroupedColumns[] = groups.map((group) => {
    const columns: DataFieldWithDataType[] = allowedFields.filter((f) => {
      const groupName = group.id.replace('-', '_').toUpperCase();
      return f.dataField.includes(groupName);
    });
    alreadyGroupedColumns = [...alreadyGroupedColumns, ...columns];
    group['subItems'] = columns;
    return group;
  });
  groupedColumns.push({
    id: 'other',
    nameKey: 'pages:reports.columnSelector.searchableView.groupHeaders.other',
    subItems: allowedFields.filter((f) => !alreadyGroupedColumns.includes(f)),
  });
  return groupedColumns.filter((c) => !!c.subItems?.length);
};

interface ColumnItemProps {
  item: GroupedColumns;
  defaultExpanded: boolean;
  onFieldSelected: any;
  columnsActive: DataFieldWithDataType[];
  setColumnsActive: (columns: DataFieldWithDataType[]) => void;
  isFieldDisabled: (field: DataFieldWithDataType) => boolean;
}

const ColumnItem = (props: ColumnItemProps) => {
  const { t } = useTranslation();
  const formatFieldLabel = useFieldLabelFormatter();
  const {
    item,
    defaultExpanded,
    onFieldSelected,
    columnsActive,
    setColumnsActive,
    isFieldDisabled,
  } = props;
  const [isExpanded, setExpanded] = useState(defaultExpanded);
  const [checked, setChecked] = useState(false);
  const { nameKey, subItems } = item;
  // Disabled fields can never be selected, so they must not count towards this group's
  // selected total or its select-all state — otherwise a group containing one would never
  // read as fully selected.
  const selectableSubItems = (subItems ?? []).filter(
    (sI) => !isFieldDisabled(sI)
  );
  const newColumnsActive = selectableSubItems.filter((sI) =>
    columnsActive.some(
      (ca) => ca.dataType === sI.dataType && ca.dataField === sI.dataField
    )
  );
  const totalSelected = newColumnsActive.length;

  useEffect(() => {
    setChecked(isCheckAll());
  }, []);

  const isCheckAll = () => {
    return (
      selectableSubItems.length > 0 &&
      totalSelected === selectableSubItems.length
    );
  };
  const toggleExpand = () => {
    setExpanded(!isExpanded);
  };
  const onToggleAll = () => {
    if (checked) {
      setColumnsActive([
        ...columnsActive.filter(
          (c) => !selectableSubItems.some((i) => isEqual(c, i))
        ),
      ]);
    } else {
      setColumnsActive(
        uniqBy([...columnsActive, ...selectableSubItems], 'dataField')
      );
    }
    setChecked(!checked);
  };
  const itemTitle = totalSelected
    ? `${t(nameKey)} (${totalSelected} ${t('pages:reports.columnSelector.searchableView.selected')})`
    : t(nameKey);

  return (
    <>
      <DenseListItem role="button" dense disableGutters onClick={onToggleAll}>
        <Checkbox
          sx={{ color: defaultTheme.color.clientPrimary }}
          checked={isCheckAll()}
          tabIndex={-1}
          disableRipple
        />
        <ListItemText primary={itemTitle} />
        <ListItemSecondaryAction>
          <IconButton
            aria-label="toggle expand"
            onClick={toggleExpand}
            size="large"
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </ListItemSecondaryAction>
      </DenseListItem>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        {subItems && subItems.length > 0 && (
          <List component={'div' as 'ul'} dense disablePadding>
            {subItems
              .sort((a, b) =>
                sortAlphabeticalComparator(true)(a.dataField, b.dataField)
              )
              .map((subItem: DataFieldWithDataType) => {
                const disabled = isFieldDisabled(subItem);
                const label = formatFieldLabel(subItem);
                const row = (
                  <DenseListItemButton
                    key={getKeyFromDataFieldWithDataType(subItem)}
                    dense
                    disabled={disabled}
                    onClick={disabled ? undefined : onFieldSelected(subItem)}
                  >
                    <Checkbox
                      sx={{ color: defaultTheme.color.clientPrimary }}
                      checked={
                        !disabled &&
                        columnsActive.some((c) => isEqual(c, subItem))
                      }
                      disabled={disabled}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText primary={label} />
                    {disabled && (
                      <LockOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'text.disabled' }}
                        aria-hidden
                      />
                    )}
                  </DenseListItemButton>
                );
                return disabled ? (
                  <Tooltip
                    key={getKeyFromDataFieldWithDataType(subItem)}
                    title={t(
                      'pages:reports.columnSelector.searchableView.noAccess'
                    )}
                  >
                    {/* Tooltip needs a non-disabled node to receive hover events. */}
                    <span>{row}</span>
                  </Tooltip>
                ) : (
                  row
                );
              })}
          </List>
        )}
      </Collapse>
    </>
  );
};

/** One draggable row in the selected-columns list, when `reorderable` is on. */
const SortableSelectedField = (props: {
  id: string;
  label: string;
  onRemove: () => void;
}) => {
  const { id, label, onRemove } = props;
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <ListItem
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        background: lightTeal,
        borderRadius: 4,
        marginBottom: 4,
      }}
      secondaryAction={
        <IconButton edge="end" onClick={onRemove} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    >
      <span
        {...attributes}
        {...listeners}
        style={{ display: 'flex', cursor: 'grab', marginRight: 8 }}
      >
        <DragIndicatorIcon fontSize="small" />
      </span>
      <ListItemText primary={label} />
    </ListItem>
  );
};

// Add a styled Dialog component
const StyledSearchDialog = styled(Dialog)`
  /* Add styles to prevent the initial positioning flash */
  & .MuiDialog-container {
    opacity: 1;
    transition: opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  }
` as typeof Dialog;

const SearchableColumnSelector = observer(
  (props: SearchableColumnSelectorProps) => {
    const { t } = useTranslation();
    const aliases = useAliasesContext();
    const locale = useGlobalLocaleContext();
    const selectedLanguage = locale.selected;
    const dashboardTracker = useDashboardTrackerContext();
    const {
      onClose,
      handleApplyFields,
      useNewUI,
      toggleNewUI,
      selectedFields,
      allowedFields,
      disabledFields = [],
      reorderable = false,
    } = props;

    const [inputValue, setInputValue] = useState('');
    const [columnsActive, setColumnsActive] = useState<DataFieldWithDataType[]>(
      []
    );
    const sensors = useSensors(useSensor(PointerSensor));

    const disabledKeys = useMemo(
      () => new Set(disabledFields.map(getKeyFromDataFieldWithDataType)),
      [disabledFields]
    );
    const isFieldDisabled = useCallback(
      (field: DataFieldWithDataType) =>
        disabledKeys.has(getKeyFromDataFieldWithDataType(field)),
      [disabledKeys]
    );

    const onDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = columnsActive.findIndex(
        (f) => getKeyFromDataFieldWithDataType(f) === active.id
      );
      const newIndex = columnsActive.findIndex(
        (f) => getKeyFromDataFieldWithDataType(f) === over.id
      );
      if (oldIndex < 0 || newIndex < 0) return;
      setColumnsActive(arrayMove(columnsActive, oldIndex, newIndex));
    };

    useEffect(() => {
      const newActiveColumns = selectedFields.filter((f) =>
        allowedFields.some(
          (af) => af.dataType === f.dataType && af.dataField === f.dataField
        )
      );
      setColumnsActive(
        uniqWith([...columnsActive, ...newActiveColumns], isEqual)
      );
    }, [selectedFields]);

    const onFieldSelected = (field: DataFieldWithDataType) => () => {
      const index = columnsActive.findIndex((c) => isEqual(c, field));
      if (index > -1) {
        columnsActive.splice(index, 1);
        setColumnsActive([...columnsActive]);
      } else {
        setColumnsActive([...columnsActive, field]);
      }
    };
    const onSubmit = () => {
      dashboardTracker.trackChooseColumns('new');
      handleApplyFields(columnsActive);
    };
    const renderAside = () => {
      const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
      };
      const allColumns = getGroupedColumns(allowedFields);
      const filterFunc = (c: GroupedColumns) => {
        const { subItems } = c;

        if (inputValue) {
          return {
            ...c,
            subItems: subItems?.filter((sI: DataFieldWithDataType) => {
              return isDataFieldMatchedInSearch(sI, inputValue, {
                alias: getAliasForDataField(aliases, selectedLanguage)(sI),
              });
            }),
          };
        }

        return c;
      };
      const allFilteredColumns = allColumns
        .map(filterFunc)
        .filter((c) => c.subItems?.length);

      return (
        <Grid container style={{ paddingRight: '20px' }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t(
                'pages:reports.columnSelector.searchableView.searchFieldLabel'
              )}
              placeholder={t(
                'pages:reports.columnSelector.searchableView.searchFieldPlaceholder'
              )}
              value={inputValue}
              onChange={onChange}
              margin="normal"
              slotProps={{
                inputLabel: {
                  style: {
                    color: '#333',
                  },
                },
              }}
            />
          </Grid>
          <Grid item container>
            <Grid item xs={12} style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              <List>
                {allFilteredColumns.map((c) => (
                  <ColumnItem
                    key={c.id}
                    item={c}
                    defaultExpanded={true}
                    columnsActive={columnsActive}
                    setColumnsActive={setColumnsActive}
                    onFieldSelected={onFieldSelected}
                    isFieldDisabled={isFieldDisabled}
                  />
                ))}
              </List>
            </Grid>
          </Grid>
        </Grid>
      );
    };
    const fieldLabel = useFieldLabelFormatter();

    const renderMainContent = () => {
      if (!reorderable) {
        return (
          <>
            {columnsActive.map((field) => (
              <Chip
                color="primary"
                key={getKeyFromDataFieldWithDataType(field)}
                label={fieldLabel(field)}
                style={{ margin: '5px' }}
              />
            ))}
          </>
        );
      }
      const ids = columnsActive.map(getKeyFromDataFieldWithDataType);
      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <List dense disablePadding>
              {columnsActive.map((field) => (
                <SortableSelectedField
                  key={getKeyFromDataFieldWithDataType(field)}
                  id={getKeyFromDataFieldWithDataType(field)}
                  label={fieldLabel(field)}
                  onRemove={onFieldSelected(field)}
                />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      );
    };
    const totalSelected = columnsActive.length;

    return (
      <StyledSearchDialog
        open
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        aria-labelledby="Column Selector Dialog"
        style={{ position: 'absolute' }}
      >
        <DialogTitle
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" component="div">
            {t('pages:reports.columnSelector.chooseColumnsV2')}
          </Typography>

          {toggleNewUI && (
            <Button variant="contained" color="primary" onClick={toggleNewUI}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'secondary.main',
                }}
              >
                {t(
                  `pages:reports.columnSelector.${(useNewUI && 'useOldView') || 'useNewView'}`
                )}
              </Typography>
            </Button>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid item xs={5}>
              {renderAside()}
            </Grid>
            <Grid item xs={7}>
              <Typography variant="h5" component="div" paragraph>
                {t(`pages:reports.columnSelector.searchableView.totalSelected`)}
                : {totalSelected}
              </Typography>
              <Grid style={{ height: '50vh', overflowY: 'auto' }}>
                {renderMainContent()}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            fullWidth
            color="primary"
            onClick={onSubmit}
          >
            {t('pages:reports.columnSelector.applyButton')}
          </Button>
        </DialogActions>
      </StyledSearchDialog>
    );
  }
);

export default SearchableColumnSelector;
